import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import BusinessPicker from "@/components/admin/BusinessPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, Camera, ClipboardList, CheckCircle2, AlertTriangle, Route, MapPinned, X, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ManifestScan() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [booking, setBooking] = useState(false);
  const [done, setDone] = useState(null);
  const [savedBiz, setSavedBiz] = useState([]);

  const loadSaved = useCallback(() => api.get("/manifest/addresses").then((r) => setSavedBiz(r.data)).catch(() => {}), []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const deleteSavedBiz = async (id) => {
    try {
      await api.delete(`/manifest/addresses/${id}`);
      setSavedBiz((p) => p.filter((b) => b.id !== id));
      toast.success("Saved business removed — it'll need an address next scan.");
    } catch { toast.error("Couldn't remove that business"); }
  };

  const scan = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setScanning(true); setResult(null); setDone(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/manifest/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult({ ...data, stops: data.stops.map((s) => ({ ...s, approved: s.verified, editing: !s.verified })) });
      toast.success(`Read ${data.line_count} lines — ${data.stops.length} stops to review and approve.`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't read the manifest"); }
    finally { setScanning(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const patchStop = (idx, patch) => setResult((r) => ({ ...r, stops: r.stops.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }));

  const fixStop = (idx, addr) => patchStop(idx, {
    line1: addr.line1 || "", city: addr.city || "", postcode: addr.postcode || "",
    lat: addr.lat, lng: addr.lng, verified: addr.lat != null, editing: false,
    approved: addr.lat != null, source: "manual",
  });

  const pickBusiness = (idx, b) => patchStop(idx, {
    business: b.trade_name, line1: b.address.line1 || "", city: b.address.city || "", postcode: b.address.postcode || "",
    lat: b.address.lat, lng: b.address.lng, verified: b.address.lat != null, approved: b.address.lat != null, editing: false, source: "directory", business_id: b.id,
  });

  const addStop = () => setResult((r) => ({
    ...r,
    stops: [...r.stops, { business: "", invoices: [], line1: "", city: "", postcode: "", lat: null, lng: null, verified: false, approved: false, editing: true, source: "manual", added: true }],
  }));

  const removeStop = (idx) => setResult((r) => ({ ...r, stops: r.stops.filter((_, i) => i !== idx) }));

  const approvedStops = result?.stops.filter((s) => s.approved && s.verified && s.business.trim()) || [];

  const book = async () => {
    setBooking(true);
    try {
      const { data } = await api.post("/manifest/book", {
        manifest_no: result.manifest_no,
        stops: approvedStops.map((s) => ({ business: s.business.trim(), invoices: s.invoices, line1: s.line1, city: s.city, postcode: s.postcode, lat: s.lat, lng: s.lng })),
      });
      setDone(data);
      loadSaved();
      toast.success(`${data.count} deliveries queued to ${data.customer}'s account.`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't queue the deliveries"); }
    finally { setBooking(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="manifest-back-button" className="mb-4 gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-kilted-green">Scan a manifest</h1>
        <p className="mt-1 text-sm text-muted-foreground">Photograph a delivery manifest — we read every stop, queue them as Repco on-account deliveries from the Cooma depot, and add them to the optimized route.</p>

        <Card className="mt-6 border-kilted-gold/40 bg-kilted-gold/5">
          <CardContent className="pt-6">
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={scan} data-testid="manifest-file-input" />
            <Button type="button" variant="outline" disabled={scanning} onClick={() => fileRef.current?.click()} data-testid="manifest-scan-button"
              className="w-full gap-2 border-kilted-gold text-kilted-green hover:bg-kilted-gold/10">
              {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              {scanning ? "Reading manifest…" : "Upload / take a manifest photo"}
            </Button>
          </CardContent>
        </Card>

        {result && !done && (
          <Card className="mt-6" data-testid="manifest-review-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><ClipboardList className="h-5 w-5 text-kilted-gold" /> Review &amp; approve {result.stops.length} stops {result.manifest_no ? `· Manifest ${result.manifest_no}` : ""}</CardTitle>
              <p className="text-sm text-muted-foreground">Tick the stops for this run. Fix any name or address, or add a stop the scan missed — including private (non-business) recipients.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {result.stops.map((s, i) => (
                <div key={s.added ? `added-${i}` : s.business || i} className={`rounded-lg border p-3 ${s.verified ? "border-emerald-200 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`} data-testid={`manifest-stop-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <label className="flex min-w-0 flex-1 items-center gap-2.5">
                      <Checkbox checked={s.approved} disabled={!s.verified || !s.business.trim()}
                        onCheckedChange={(v) => patchStop(i, { approved: !!v })} data-testid={`manifest-stop-approve-${i}`} />
                      <Input value={s.business} onChange={(e) => patchStop(i, { business: e.target.value })}
                        placeholder="Business or recipient name" data-testid={`manifest-stop-name-${i}`}
                        className="h-8 border-transparent bg-transparent px-1 font-semibold shadow-none focus-visible:border-input focus-visible:bg-white" />
                    </label>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge className={s.verified ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} data-testid={`manifest-stop-status-${i}`}>
                        {s.verified ? (s.source === "saved" ? "Saved address" : s.source === "directory" ? "From directory" : s.source === "manual" ? "Address set" : "Address verified") : "Needs address"}
                      </Badge>
                      <button type="button" onClick={() => removeStop(i)} data-testid={`manifest-stop-remove-${i}`} className="text-muted-foreground hover:text-red-600" title="Remove stop">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {s.invoices.length > 0 && (
                    <p className="ml-7 text-xs text-muted-foreground">{s.invoices.length} invoice{s.invoices.length === 1 ? "" : "s"}: {s.invoices.join(", ")}</p>
                  )}
                  {s.verified && !s.editing ? (
                    <div className="ml-7 mt-1 flex items-center justify-between gap-2">
                      <p className="flex items-center gap-1.5 text-sm text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> {[s.line1, s.city, s.postcode].filter(Boolean).join(", ")}</p>
                      <button type="button" onClick={() => patchStop(i, { editing: true })} data-testid={`manifest-stop-edit-${i}`}
                        className="flex shrink-0 items-center gap-1 text-xs font-medium text-kilted-green hover:underline">
                        <Pencil className="h-3 w-3" /> Change
                      </button>
                    </div>
                  ) : (
                    <div className="ml-7 mt-2 space-y-2">
                      <BusinessPicker which={`stop-${i}`} onPick={(b) => pickBusiness(i, b)} placeholder="Business Directory — type a name to fill name + address…" />
                      <AddressAutocomplete label={s.verified ? "Pick the correct address" : "Find the address"} onSelect={(a) => fixStop(i, a)} testid={`manifest-fix-${i}`} />
                      {s.verified && <button type="button" onClick={() => patchStop(i, { editing: false })} className="mt-1 text-xs text-muted-foreground hover:underline" data-testid={`manifest-stop-cancel-edit-${i}`}>Keep current address</button>}
                    </div>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addStop} data-testid="manifest-add-stop-button" className="w-full gap-1.5 border-dashed">
                <Plus className="h-4 w-4" /> Add a stop (person or business not on the scan)
              </Button>
              {approvedStops.length < result.stops.length && (
                <p className="flex items-center gap-1.5 text-xs text-amber-700" data-testid="manifest-unverified-note"><AlertTriangle className="h-3.5 w-3.5" /> {result.stops.length - approvedStops.length} stop(s) unticked, unnamed or missing an address — they won't be added to the run.</p>
              )}
              <Button onClick={book} disabled={booking || approvedStops.length === 0} data-testid="manifest-book-button" className="w-full gap-2 bg-kilted-gold text-kilted-green hover:bg-kilted-goldLight">
                {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />} Approve &amp; add {approvedStops.length} stop{approvedStops.length === 1 ? "" : "s"} to the run
              </Button>
            </CardContent>
          </Card>
        )}

        {done && (
          <Card className="mt-6 border-emerald-200" data-testid="manifest-done-card">
            <CardContent className="space-y-3 pt-6">
              <p className="flex items-center gap-2 font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> {done.count} deliveries queued on {done.customer}'s monthly account</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {done.created.map((c) => <li key={c.order_id}>{c.tracking_number} — {c.business} (${c.total.toFixed(2)})</li>)}
              </ul>
              {user?.role === "driver" && (
                <Button onClick={() => navigate("/driver/route")} data-testid="manifest-view-route-button" className="w-full gap-2 bg-kilted-green hover:bg-kilted-greenLight"><Route className="h-4 w-4" /> View optimized route</Button>
              )}
            </CardContent>
          </Card>
        )}

        {savedBiz.length > 0 && (
          <Card className="mt-6" data-testid="manifest-saved-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><MapPinned className="h-4 w-4 text-kilted-gold" /> Remembered businesses</CardTitle>
              <p className="text-sm text-muted-foreground">These auto-verify on every future manifest. Queuing a run saves new addresses automatically.</p>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {savedBiz.map((b) => (
                <div key={b.id} className="flex items-center gap-1.5 rounded-full border border-kilted-green/30 bg-white py-1 pl-3 pr-1.5 text-xs" data-testid={`saved-biz-${b.id}`}>
                  <span className="font-medium text-kilted-green">{b.business}</span>
                  <span className="text-muted-foreground">· {[b.line1, b.city].filter(Boolean).join(", ")}</span>
                  <button type="button" onClick={() => deleteSavedBiz(b.id)} data-testid={`delete-saved-biz-${b.id}`} className="rounded-full p-0.5 text-muted-foreground hover:text-red-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
